import { useEffect, useMemo, useState } from 'preact/hooks';
import { OwnershipMark, ownershipFromRule } from '@vowric/ui';

/** The manifest field-rule shape, extracted STRUCTURALLY off the reducer's
 * own signature — this package deliberately has no backend workspace
 * dependency (the api.ts convention), and duplicating the interface would
 * be the drift the reducer exists to prevent. */
type AuthorityFieldRule = NonNullable<Parameters<typeof ownershipFromRule>[0]>;
import { BackLink } from '../components/BackLink';
import { settingsCardOwnedBy, type PackVertical } from '../routes/route';
import { PasswordForm, WorkspaceSwitcher, usePasswordDoor } from './Account';
import type { TenantMembership } from '../lib/api';
import { getAgentAccess, rotateAgentKey, ApiError as AgentApiError, type AgentAccessInfo } from '../lib/api';
import { getPlanPolicy, setPlanPolicy, type PlanPolicyApi } from '../lib/api';
import { InvitePanel, type InviteSeat } from '../components/TeamDoor';
import { ConnectedAccounts } from './ConnectedAccounts';
import { canEditTechs } from './Techs';
import {
  getEntryConfig,
  getFirstRun,
  getMyRecord,
  getTeamMe,
  updateMyName,
  type PackJobRole,
  type TeamMember,
  listMemberPreferences,
  setMemberPreference,
  listDeviceSessions,
  revokeDeviceSession,
  listNotificationPreferences,
  setNotificationPreference,
  enrolMemberTelegram,
  reopenFirstRunStep,
  logout,
  type ApiUser,
  type DeviceSession,
  type FirstRunLane,
  type FirstRunTicket,
  type NotificationMode,
  type RecordRow,
  getMailbox,
  getWorkAddress,
  provisionWorkAddress,
  resetWorkAddressPassword,
  getByoMailbox,
  connectByoMailbox,
  disconnectByoMailbox,
  getIcsFeedStatus,
  mintIcsFeed,
  revokeIcsFeed,
  type IcsFeedStatusApi,
  type IcsFeedMintApi,
  planHas,
  type WorkAddressStatusApi,
  type WorkAddressCredentialApi,
  type ByoMailboxStatusApi,
  getPlan,
  createBillingCheckout,
  changeBillingPlan,
  openBillingPortal,
  ApiError,
  type EntryConfig,
  type PlanApi,} from '../lib/api';
import { setTheme, applyTheme } from '../lib/theme';
import { disablePush, enablePush, readPushState, type PushState } from '../lib/pushEnrolment';
import './Settings.css';

export interface SettingsProps {
  tenantId: string;
  user: ApiUser;
  onBack: () => void;
  /** B17d, carried across at the 2026-08-26 Rexx merge. Switching workspace
   *  is App's act — it is the one writer of the stored tenant id + vertical —
   *  so this screen only asks. Optional because a deployment with a single
   *  membership never needs it, and Rexx's own App does not pass it. */
  onSwitchTenant?: ((tenant: TenantMembership) => void) | undefined;
  /** Told when the person renames themselves, so the shell's copy of the user
   * updates too. Without it the name saves correctly and NOTHING visibly
   * changes until a reload — Today kept greeting the walker by their email
   * local part after a successful save (walked 2026-08-29). A receipt that
   * only the screen you are standing on can see is an acknowledgement, not a
   * receipt. Optional: a host that does not hold a user copy need not care. */
  onUserUpdated?: ((user: ApiUser) => void) | undefined;
  /** The booted pack (App's own `packVertical`). Decides the cards that
   * belong to some packs only — routes/route.ts's SETTINGS_CARD_OWNERS.
   * Absent = no pack-owned card renders (the safe side). */
  packVertical?: PackVertical | undefined;
  /** S1c — the Google OAuth callback's one-time outcome (`connected` |
   *  `partial` | `declined`), carried across from the server's own
   *  `/#/personal/accounts/<outcome>` redirect (App.tsx's legacy-route
   *  redirect). Undefined on an ordinary visit — no banner then. */
  connectedAccountsOutcome?: string | undefined;
}

// C6b (wf05 §03): your preferences, and the file the company keeps on you —
// two different things, deliberately on ONE screen with a visible line
// between them (the .stg__cols division below is that line: a designed
// split of the surface, not a decorative divider). Preferences are yours;
// the record is the company's, and EVERY field says which with an
// OwnershipMark — you / office / view only.
//
// FIELD OWNERSHIP IS THE C1-E2 DEMO-GRADE REDUCER, accepted for v1
// (spec §3: do NOT build server-computed ownership): the rules below are a
// screen-local restatement of the Rexx manifest's intent — pay_basis
// mirrors the manifest's own visible_locked rule verbatim; the rest encode
// wf05 §03's marks (identity facts are the office's, reachability facts
// are yours). When the manifest's field rules grow to cover these, this
// map is what gets deleted.
const RECORD_RULES: Record<string, AuthorityFieldRule> = {
  name: { read: '*', write: ['admin'] },
  role: { read: '*', write: ['admin'] },
  phone: { read: '*', write: ['tech', 'admin'] },
  email: { read: '*', write: ['tech', 'admin'] },
  emergency_contact: { read: '*', write: ['tech', 'admin'] },
  pay_basis: { read: ['owner', 'admin', 'dispatcher'], write: ['owner'], mask: 'visible_locked' },
};

type Appearance = 'auto' | 'light' | 'dark';

/** Client-owned defaults — a key never set is simply absent server-side
 * (the row would otherwise fake "the user chose this"). */
const DEFAULTS = {
  hold_to_talk: true,
  confirm_before_filing: false,
  appearance: 'auto' as Appearance,
};

const APPEARANCE_WORD: Record<Appearance, string> = { auto: 'Auto', light: 'Light', dark: 'Dark' };

/* -------------------------------------------------------------------- *
 * CV6b §3 — NOTIFICATIONS, FOR REAL.
 *
 * TWO CONTROLS WERE REMOVED HERE, and the removal is the point rather than
 * a side effect. C6b drew a "Notify me about — Both / Jobs / Messages"
 * chip and a "Quiet hours — nothing between these" pair. Both wrote
 * member_preferences keys (`notify_about`, `quiet_hours`) that NOTHING ever
 * read: verified across core, server, web and the packs — the only readers
 * were this file's own render. They were harmless while the app could not
 * ping anybody. CV6b makes the phone actually buzz, and at that moment they
 * stop being dead and start being FALSE: a person who sets quiet hours and
 * is woken at 2am has been lied to by a control, which is worse than not
 * having one. So the shape that reflects what the server truly holds
 * replaces them, and the two keys are simply no longer written. (Real quiet
 * hours are a server-side scheduling decision — notifyThreshold's business —
 * and belong on the register, not in a client that cannot honour them.)
 *
 * THREE CATEGORIES, THREE MODES. `chat`, `work` and `attention` are the
 * categories CV6a's dispatchers actually write; 'immediate' | 'digest' |
 * 'off' are the modes the server stores. A two-state toggle cannot hold
 * three modes: it would silently rewrite somebody's 'digest' as
 * 'immediate' the first time they touched an unrelated row, so this is a
 * three-way chip — the same cycling control the Appearance row already uses.
 *
 * ⚑ THE KEYS ARE CORE-OWNED, THE WORDS ARE THE PACK'S. `chat`, `work` and
 * `attention` are what CV6a's dispatchers write and no pack may invent a
 * fourth. What they are CALLED was a trade company's until 2026-09-01 — "Job
 * threads and the office" rendered on a household's screen — so the two lines
 * come from the pack's entry config, and these are the neutral fallbacks a
 * deployment that declares nothing gets.
 * -------------------------------------------------------------------- */

const NOTIFY_KEYS = ['chat', 'work', 'attention'] as const;
type NotifyKey = (typeof NOTIFY_KEYS)[number];

const NOTIFY_NEUTRAL: Record<NotifyKey, { label: string; sub: string }> = {
  chat: { label: 'Messages', sub: 'Anything written to you' },
  work: { label: 'Your work', sub: 'What you are on, as it changes' },
  attention: { label: 'Needs you', sub: 'Anything waiting on you' },
};

/** The invite door's words where a pack declares none. Never an office and
 * never a screen: whoever this pack seats above its members, plainly. */
const INVITE_NEUTRAL = {
  section: 'Your people',
  mini: 'you invite · they accept',
  label: 'Someone alongside you',
} as const;

const MODE_ORDER: NotificationMode[] = ['immediate', 'digest', 'off'];
const MODE_WORD: Record<NotificationMode, string> = {
  immediate: 'Right away',
  digest: 'Daily',
  off: 'Off',
};

/** The server's OWN default for a category nobody has set (preferences.ts:
 * `rows[0]?.mode ?? 'immediate'`). An absent row is NOT "off", and painting
 * it as off would tell someone they had switched something off that is in
 * fact on. */
const NOTIFY_DEFAULT_MODE: NotificationMode = 'immediate';

/** What the push row says for each state it can be in. Four of the six can
 * never produce a subscription and each says so plainly instead of offering
 * a control that fails silently. */
const PUSH_WORD: Record<PushState, string> = {
  on: 'On',
  off: 'Off',
  blocked: 'Blocked on this phone',
  'needs-install': 'Add to home screen first',
  unsupported: 'Not on this browser',
  unconfigured: 'Not set up here',
};

/** THE STEPS THAT CAN HONESTLY BE RE-ANSWERED (Huan, 2026-08-25).
 *
 * Not every step should be offered again. `identity` is answered once and
 * `first_capture` is a thing that already happened; re-opening either would
 * be theatre. These three are different — each is true of a DEVICE or a
 * MOMENT, not of a person:
 *
 *   install_app  the phone in your hand, which may not be the one you set up on
 *   push_pings   a permission belonging to THIS device
 *   tickets      licences, which expire
 *
 * Filtered against the pack's own script before rendering, so a build that
 * scripts none of them shows no section at all.
 */
const REOPENABLE_STEPS: Array<{ key: string; label: string; sub: string }> = [
  { key: 'install_app', label: 'Add to home screen', sub: 'on the phone you are holding' },
  { key: 'push_pings', label: 'Notifications', sub: 'permission for this device' },
  { key: 'tickets', label: 'Licences', sub: 'add or renew what you hold' },
];

/** A coarse, honest device label off the stored user agent — recognition,
 * never fingerprinting. Null UA (pre-0043 sessions) stays unnamed. */
export function deviceLabel(userAgent: string | null): string {
  if (!userAgent) return 'Device';
  if (/iPhone/i.test(userAgent)) return 'iPhone';
  if (/iPad/i.test(userAgent)) return 'iPad';
  if (/Android/i.test(userAgent)) return 'Android';
  if (/Mac OS X|Macintosh/i.test(userAgent)) return 'Mac';
  if (/Windows/i.test(userAgent)) return 'Windows PC';
  if (/Linux/i.test(userAgent)) return 'Linux';
  return 'Device';
}

function shortDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  // The reader's own locale, not a Canadian one — the same date on a phone
  // set to anything else read as somebody else's format.
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** Days until an ISO date — the tickets pane's expiry arithmetic. */
function daysUntil(iso: string): number {
  return Math.floor((new Date(iso).getTime() - Date.now()) / 86_400_000);
}

/** Amber when inside 120 days — wf05 draws 98 d already amber; the REAL
 * ramp (an attention item at threshold) is the office's and is register
 * debt, not this screen's. */
const EXPIRY_AMBER_DAYS = 120;

/** S8: pay left hvac_tech_profile for the admin-read hvac_pay_profile, so
 * the server no longer hands this screen pay at all — the row renders only
 * if a value actually arrived (older records, other packs), never a
 * permanent '—' asserting a fact the server declined to return. */
function payBasisLine(record: RecordRow): string | null {
  const commission = record.fields.commission_rate;
  if (typeof commission === 'number') return `Commission ${commission}%`;
  const hourly = record.fields.hourly_rate;
  if (typeof hourly === 'number') return `$${hourly}/h`;
  return null;
}


/** Read-only agent access (2026-09-01) — OWNER-ONLY, self-gating: the server
 * answers 403 for anyone else and this card renders nothing. The full key is
 * shown exactly once, at mint time; afterwards only its prefix survives.
 * Copy and Rotate on the card (Huan's spec, verbatim). */
function AgentAccessCard({ tenantId }: { tenantId: string }) {
  const [info, setInfo] = useState<AgentAccessInfo | null>(null);
  const [forbidden, setForbidden] = useState(false);
  const [freshKey, setFreshKey] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);

  useEffect(() => {
    getAgentAccess(tenantId)
      .then(setInfo)
      .catch((e) => {
        if (e instanceof AgentApiError && e.status === 403) setForbidden(true);
      });
  }, [tenantId]);

  if (forbidden || !info) return null;

  async function copy(label: string, value: string) {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(label);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      /* clipboard refused — the value is on screen to select by hand */
    }
  }

  async function rotate() {
    if (busy) return;
    setBusy(true);
    try {
      const minted = await rotateAgentKey(tenantId);
      setFreshKey(minted.key);
      setInfo({ url: minted.url, hasKey: true, keyPrefix: minted.keyPrefix, createdAt: new Date().toISOString() });
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div class="stg__sec stg__sec--mt">
        <span>AI agent access</span>
        <span class="stg__mini">read-only</span>
      </div>
      <div class="stg__row stg__row--block">
        <div class="stg__mini">Connection URL</div>
        <div class="stg__agent-line">
          <code>{info.url}</code>
          <button type="button" class="stg__btn" onClick={() => void copy('url', info.url)}>
            {copied === 'url' ? 'Copied' : 'Copy'}
          </button>
        </div>
        <div class="stg__mini">API key</div>
        {freshKey ? (
          <div class="stg__agent-line">
            <code>{freshKey}</code>
            <button type="button" class="stg__btn" onClick={() => void copy('key', freshKey)}>
              {copied === 'key' ? 'Copied' : 'Copy'}
            </button>
          </div>
        ) : (
          <div class="stg__agent-line">
            <code>{info.hasKey ? info.keyPrefix : 'No key yet'}</code>
          </div>
        )}
        {freshKey ? (
          <div class="stg__mini">This is the only time the full key is shown — copy it now.</div>
        ) : null}
        <div class="stg__agent-line">
          <button type="button" class="stg__btn" disabled={busy} onClick={() => void rotate()}>
            {info.hasKey ? 'Rotate key' : 'Generate key'}
          </button>
        </div>
        {info.hasKey ? (
          <div class="stg__mini">Rotating revokes the current key immediately.</div>
        ) : null}
      </div>
    </>
  );
}

/** K5 — Plan policy (c6-plan-authorization.md: "Settings -> Plan policy
 * (levers): the three numbers; blank = no policy, said on screen."). Self-
 * fetching like AgentAccessCard above; the server 403s a non-admin caller
 * on the PUT, so a member sees the numbers read-only rather than the card
 * vanishing outright (unlike AgentAccessCard's owner-only gate, a plan
 * policy is not a secret — everyone doing the work benefits from seeing
 * the ceiling, only an admin may move it). */
function PlanPolicyCard({ tenantId }: { tenantId: string }) {
  const [policy, setPolicy] = useState<PlanPolicyApi | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [maxHours, setMaxHours] = useState('');
  const [marginFloor, setMarginFloor] = useState('');
  const [lookahead, setLookahead] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  useEffect(() => {
    getPlanPolicy(tenantId)
      .then((p) => {
        setPolicy(p);
        setLoaded(true);
        if (p) {
          setMaxHours(String(p.maxCommittedHoursPerWeek));
          setMarginFloor(p.marginFloorPct === null ? '' : String(p.marginFloorPct));
          setLookahead(String(p.lookaheadWeeks));
        }
      })
      .catch(() => setLoaded(true));
  }, [tenantId]);

  async function save() {
    if (!maxHours.trim() || !lookahead.trim()) {
      setNote('max committed hours and lookahead weeks are both required — margin floor may be left blank');
      return;
    }
    setBusy(true);
    setNote(null);
    try {
      const saved = await setPlanPolicy(tenantId, {
        maxCommittedHoursPerWeek: Number(maxHours),
        marginFloorPct: marginFloor.trim() ? Number(marginFloor) : null,
        lookaheadWeeks: Number(lookahead),
      });
      setPolicy(saved);
    } catch (err) {
      setNote(err instanceof AgentApiError ? err.message : 'that policy was refused');
    } finally {
      setBusy(false);
    }
  }

  if (!loaded) return null;

  return (
    <>
      <div class="stg__sec stg__sec--mt">
        <span>Plan policy</span>
        <span class="stg__mini">{policy ? 'set' : 'blank = no policy — every approval is ordinary'}</span>
      </div>
      <div class="stg__row stg__row--block">
        <div class="stg__mini">Max committed hours, per week</div>
        <input
          class="stg__input"
          type="number"
          value={maxHours}
          disabled={busy}
          onInput={(e) => setMaxHours((e.target as HTMLInputElement).value)}
        />
        <div class="stg__mini">Margin floor, percent (blank = no margin check)</div>
        <input
          class="stg__input"
          type="number"
          value={marginFloor}
          disabled={busy}
          onInput={(e) => setMarginFloor((e.target as HTMLInputElement).value)}
        />
        <div class="stg__mini">Lookahead, weeks</div>
        <input
          class="stg__input"
          type="number"
          value={lookahead}
          disabled={busy}
          onInput={(e) => setLookahead((e.target as HTMLInputElement).value)}
        />
        <button type="button" class="stg__btn" disabled={busy} onClick={() => void save()}>
          {policy ? 'Update' : 'Set the policy'}
        </button>
        {note ? <div class="stg__mini">{note}</div> : null}
      </div>
    </>
  );
}

/** How full the household is, guarded against a zero cap. One function so the
 * bar's width, its colour and the WORD beside it can never disagree. */
function storageShare(usedBytes: number, capBytes: number): number {
  return usedBytes / Math.max(1, capBytes);
}

/** Bytes as a human line for the meter row. GB at one decimal reads like the
 * plans page; under a gigabyte, MB keeps a fresh household's number honest
 * instead of "0.0 GB". */
function formatGb(bytes: number): string {
  const gb = bytes / (1024 * 1024 * 1024);
  if (gb >= 1 || bytes === 0) return `${gb >= 100 ? Math.round(gb) : Math.round(gb * 10) / 10} GB`;
  const mb = bytes / (1024 * 1024);
  return `${Math.max(1, Math.round(mb))} MB`;
}

/** The password block, header and form as ONE unit: the door decides for
 * both (pack posture OR an actually-held legacy credential — Account's
 * usePasswordDoor). A header standing over nothing labelled an empty band on
 * every passwordless household, which is every CallToWrap household. */
function AccountPasswordSection({ email }: { email: string }) {
  const door = usePasswordDoor();
  if (!door) return null;
  return (
    <>
      <div class="stg__sec stg__sec--mt">
        <span>Your account</span>
        <span class="stg__mini">{email}</span>
      </div>
      <div class="stg__row stg__row--block">
        <PasswordForm />
      </div>
    </>
  );
}

export function Settings({
  tenantId,
  user,
  onBack,
  onSwitchTenant,
  onUserUpdated,
  packVertical,
  connectedAccountsOutcome,
}: SettingsProps) {
  const [mailbox, setMailbox] = useState<string | null>(null);
  const [plan, setPlan] = useState<PlanApi | null>(null);
  // W1 — the WORK address (decision `80350c67`). Distinct from `mailbox`
  // above, which is the free inbound handle; this is the real mailbox the
  // family sends from. Null until read, and it stays null on every deployment
  // with no mailbox broker, where the route 404s.
  const [workAddress, setWorkAddress] = useState<WorkAddressStatusApi | null>(null);
  const [workBusy, setWorkBusy] = useState(false);
  const [workError, setWorkError] = useState<string | null>(null);
  // ⚑ THE PASSWORD'S ONE APPEARANCE. Held only in this component's state,
  // only from the POST that minted it until the family leaves the screen.
  // Nothing persists it and no read verb can fetch it back.
  const [workCredential, setWorkCredential] = useState<WorkAddressCredentialApi | null>(null);
  // P5 — the mailbox the family ALREADY had (plan model §6). A third thing
  // again, and none of the three is either of the others: `mailbox` is the free
  // inbound handle, `workAddress` is the account we send from, this is an
  // account on somebody else's host that we only ever READ.
  const [byo, setByo] = useState<ByoMailboxStatusApi | null>(null);
  // P6.1's door (2026-09-01): the per-user calendar feed. `icsMint` holds
  // the freshly minted link for THIS render only — the token is shown once
  // and never retrievable, so navigating away is what forgets it.
  const [ics, setIcs] = useState<IcsFeedStatusApi | null>(null);
  const [icsMint, setIcsMint] = useState<IcsFeedMintApi | null>(null);
  const [icsBusy, setIcsBusy] = useState(false);
  const [icsCopied, setIcsCopied] = useState(false);
  const [icsError, setIcsError] = useState<string | null>(null);

  const [byoBusy, setByoBusy] = useState(false);
  const [byoError, setByoError] = useState<string | null>(null);
  // ⚑ THE PASSWORD LIVES HERE AND NOWHERE ELSE ON THIS CLIENT, and only until
  // the connect call returns. Nothing persists it, no response carries it back,
  // and it is cleared the moment the mailbox connects.
  const [byoDraft, setByoDraft] = useState({ address: '', imapHost: '', imapPort: '993', password: '' });
  // B1 — the billing doors. `billingBusy` names the act in flight so two
  // taps cannot open two Checkout sessions; `billingError` is shown rather
  // than swallowed, because a family who taps Upgrade and sees nothing happen
  // assumes the product is broken.
  const [billingInterval, setBillingInterval] = useState<'month' | 'year'>('month');
  // ⚠ THE CHIP MUST START ON WHAT THEY ACTUALLY PAY. It is the interval a
  // plan change is APPLIED at, so a yearly household finding it defaulted to
  // "Monthly" would be moved to monthly by a tap that said only "Switch".
  // Synced from the subscription until the person moves it themselves; after
  // that it is their choice and nothing overwrites it.
  const [intervalTouched, setIntervalTouched] = useState(false);
  const [billingBusy, setBillingBusy] = useState<string | null>(null);
  const [billingError, setBillingError] = useState<string | null>(null);
  // What Stripe's hosted page sent them back with. `?billing=…` arrives in the
  // QUERY STRING, before the hash route (server billing/config.ts explains
  // why it cannot ride inside the hash), and it is read once and wiped so a
  // reload does not replay a receipt for a payment that already landed.
  const [billingReturn, setBillingReturn] = useState<'success' | 'cancelled' | 'managed' | null>(null);
  const [prefs, setPrefs] = useState<Record<string, unknown>>({});
  const [lanes, setLanes] = useState<FirstRunLane[]>([]);
  // C7's enrolment door, re-used HERE (Huan, 2026-08-25): the first-run walk
  // was the only place it existed, so an admin or dispatcher who set up at a
  // desk — or anyone who reached Today before connecting — had no way to
  // connect Telegram at all. This is the same route, on the screen people
  // actually go to.
  const [selfEnrol, setSelfEnrol] = useState<{ available: boolean; membershipId: string; phoneMasked: string | null } | null>(null);
  const [walkSteps, setWalkSteps] = useState<string[]>([]);
  const [enrolPhone, setEnrolPhone] = useState('');
  const [enrolBusy, setEnrolBusy] = useState(false);
  const [enrolError, setEnrolError] = useState<string | null>(null);

  // Your own name. Until 2026-08-29 a household had NO way to set this: the
  // only caller of the update lived in the first-run script, which actor_assist
  // does not declare — so `users.name` stayed null and every screen fell back
  // to the email local part ("What's up, sarah.chen?").
  const [nameDraft, setNameDraft] = useState('');
  const [nameBusy, setNameBusy] = useState(false);
  const [nameSaved, setNameSaved] = useState(false);
  const [reopening, setReopening] = useState<string | null>(null);
  // THE OFFICE DOOR (Huan, 2026-08-25). Inviting an admin from the Techs
  // screen reads wrong — that screen is about the crew. Hiring the office is
  // the owner's own act, so it lives on the owner's own page, next to the
  // password and the sign-out.
  const [me, setMe] = useState<TeamMember | null>(null);
  const [packJobRoles, setPackJobRoles] = useState<PackJobRole[]>([]);
  /** G-18 — WHICH SEAT the open panel is for, or `true` for the pack that
   * declares none and still gets the original role-picker panel. Null =
   * closed. One state, because there is one panel. */
  const [inviteOpen, setInviteOpen] = useState<InviteSeat | true | null>(null);
  /** G-17 — does this deployment's pack refuse to activate a seat with no
   * birth date on file? Read off the same /api/team/me answer the job roles
   * come from. Absent means no, and no field is drawn. */
  const [requiresMemberAge, setRequiresMemberAge] = useState(false);
  /** ⚑ MAY THIS SEAT INVITE — THE MANIFEST ANSWERING, NOT THE RANK
   * (2026-09-15, the Day 1 walk).
   *
   * `canEditTechs` asks the engine RANK, and `actor_assist` seats its second
   * adult at `admin` while reserving `actor_assist.member.invite` to `parent`.
   * So Settings drew "Your people" and the invite door for a co-parent — an
   * act the pack's manifest says is not hers, and one the invite route (gated
   * at `minimumRole: 'admin'`) would have carried out.
   *
   * `undefined` until read AND for every pack that declares no invite action,
   * which is why the gate below falls back to the rank rather than treating
   * absence as a refusal: a pack with nothing to say must not lose its door. */
  const [mayInvite, setMayInvite] = useState<boolean | undefined>(undefined);
  // WHO THE OFFICE IS, in the pack's own terms: whoever it seats ABOVE its
  // members. Never a role-name list — the web has no business knowing this
  // pack calls them 'admin' and 'dispatcher'. `owner` is excluded because a
  // second owner is not something you do from a preferences panel.
  const officeRoles = packJobRoles.filter((r) => r.seat !== 'member' && r.seat !== 'owner');
  const [sessions, setSessions] = useState<DeviceSession[]>([]);
  const [record, setRecord] = useState<RecordRow | null>(null);
  const [jobRoles, setJobRoles] = useState<string[]>([]);
  // ⚑ THE PACK'S OWN VOICE FOR THIS SCREEN (C6a's entry config). The
  // record-honesty sentence has always ridden it; since 2026-09-01 so do the
  // notification words, the invite door, the push-fallback note and whether
  // this deployment keeps a file on a person at all. Null until read, and a
  // deployment that declares nothing renders the neutral wording — never
  // another vertical's nouns.
  const [entry, setEntry] = useState<EntryConfig | null>(null);

  /** ===================================================================
   *  G-18 — THE SEATS THIS PACK OFFERS, IN ITS OWN WORDS.
   *  ===================================================================
   *  `officeRoles` above answers "who does this pack seat ABOVE its members",
   *  which is the right question for a trade deployment and the WRONG one for
   *  a household: `helper` is seated at `member` (it is a reading seat — its
   *  whole write surface is empty by design), so the filter excluded it and
   *  the one declared job role with its own rail and its own read scope
   *  appeared nowhere in the app. Nobody could invite the sibling who reads
   *  off-camera.
   *
   *  A seat is not derivable from its TIER, so the pack declares which seats
   *  its owner may offer and what each one is called. Where a pack declares
   *  nothing, the tier-derived list is still the answer and the door is byte
   *  for byte what it was.
   *
   *  ⚠ AND THE PACK DECLARES `child` NOWHERE HERE, deliberately: enrolling a
   *  performer is four calls (seat, birth date, performer record, binding)
   *  and it has its own screen. A row here that sent only the first would
   *  build half a household. */
  const declaredSeats = entry?.settings?.invite?.seats ?? null;
  /** Is there anybody to invite at all? Two questions with one answer: a pack
   * that declares its seats is asked about those, and a pack that does not is
   * asked the tier question it was always asked. */
  const canInviteSomebody = declaredSeats ? declaredSeats.length > 0 : officeRoles.length > 0;
  // CV6b §3 — the channel, and what goes down it. `push` is null until the
  // state has actually been read: rendering "Off" before we know would be a
  // guess, and this row's whole job is not to guess.
  const [push, setPush] = useState<PushState | null>(null);
  const [pushError, setPushError] = useState<string | null>(null);
  const [pushBusy, setPushBusy] = useState(false);
  /** Only the categories the SERVER holds a row for. Absent ≠ off — see
   * NOTIFY_DEFAULT_MODE. */
  const [notifyModes, setNotifyModes] = useState<Record<string, NotificationMode>>({});

  useEffect(() => {
    let cancelled = false;
    void listMemberPreferences(tenantId)
      .then((rows) => {
        if (cancelled) return;
        const map: Record<string, unknown> = {};
        for (const row of rows) map[row.key] = row.value;
        setPrefs(map);
      })
      .catch(() => {});
    // The lane rows ride the first-run endpoint's composed pack facts; a
    // build with no first-run script 404s and the section stays quiet.
    // ⚑ RENDERS NOTHING UNTIL THERE IS SOMETHING TRUE TO SHOW. `null` means
    // this deployment has no inbound mail wired, or this household has no
    // slug — and in either case the honest surface is an absent row, not a
    // placeholder. A parent who is shown an address will give it to a casting
    // director; one that bounces costs them a job and tells them nothing.
    void getMailbox(tenantId)
      .then(setMailbox)
      .catch(() => {
        // A failed read is indistinguishable from "not wired" to a family,
        // and the safe direction is the same: say nothing.
      });

    // The storage meter (F1). Same absent-not-empty posture: a failed read
    // shows no meter rather than a wrong one.
    void getPlan(tenantId)
      .then((p) => {
        if (!cancelled) setPlan(p);
      })
      .catch(() => {});

    void getFirstRun(tenantId)
      .then((s) => {
        if (cancelled) return;
        setLanes(s.lanes);
        // The enrolment door and the walk's own step list ride the SAME
        // payload the lane rows already came from — no second read.
        setSelfEnrol(s.selfEnrol);
        setWalkSteps(s.steps);
      })
      .catch(() => {});
    void listDeviceSessions()
      .then((s) => {
        if (!cancelled) setSessions(s);
      })
      .catch(() => {});
    void getMyRecord(tenantId)
      .then((r) => {
        if (!cancelled) setRecord(r);
      })
      .catch(() => {});
    void getTeamMe(tenantId)
      .then((team) => {
        if (cancelled) return;
        setJobRoles(team.member.jobRoles);
        // The same read already on this screen now also answers who the
        // caller is and what roles the pack declares — the office door needs
        // both, and a second request for facts already in hand is waste.
        setMe(team.member);
        // Prefill the field with the name already on file, so the box shows
        // what is true rather than inviting a re-type of it.
        setNameDraft(team.member.name ?? '');
        setPackJobRoles(team.packJobRoles ?? []);
        setRequiresMemberAge(team.requiresMemberAge === true);
        // ⚑ THE AUTHORITY'S OWN ANSWER (2026-09-15) — see `mayInvite` below.
        // `undefined` is a real third state: the pack declares no invite
        // action, so the rank check stands.
        setMayInvite(team.mayInvite);
      })
      .catch(() => {});
    void getEntryConfig()
      .then((cfg) => {
        if (!cancelled) setEntry(cfg);
      })
      .catch(() => {});
    // CV6b — the live push state (this device) and the server's own
    // per-category modes (this account). Two reads because they are two
    // different facts: a phone can be switched off while every category is
    // still set to ping.
    void readPushState(tenantId)
      .then((status) => {
        if (!cancelled) setPush(status.state);
      })
      .catch(() => {
        // Unknown stays unknown — the row renders nothing rather than a
        // state we did not read.
      });
    void listNotificationPreferences(tenantId)
      .then((rows) => {
        if (cancelled) return;
        const map: Record<string, NotificationMode> = {};
        for (const row of rows) map[row.category] = row.mode;
        setNotifyModes(map);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  function prefValue<K extends keyof typeof DEFAULTS>(key: K): (typeof DEFAULTS)[K] {
    const stored = prefs[key];
    return (stored === undefined ? DEFAULTS[key] : stored) as (typeof DEFAULTS)[K];
  }

  function savePref(key: string, value: unknown) {
    setPrefs((p) => ({ ...p, [key]: value }));
    void setMemberPreference(tenantId, key, value).catch(() => {});
  }

  function cycleAppearance() {
    const order: Appearance[] = ['auto', 'light', 'dark'];
    const next = order[(order.indexOf(prefValue('appearance')) + 1) % order.length]!;
    savePref('appearance', next);
    if (next === 'auto') {
      // "Follows your phone" — applied for this session; the stored
      // choice stays 'auto' (applyTheme alone never persists).
      applyTheme(window.matchMedia?.('(prefers-color-scheme: light)').matches ? 'light' : 'dark');
    } else {
      setTheme(next);
    }
  }

  /** CV6b §3 — the channel itself, turned on or off from a REAL TAP (the
   * permission prompt is only ever requested from a user gesture). Both
   * directions report the state they ended in, never the state they hoped
   * for, and a failure says what happened. */
  async function togglePush() {
    if (pushBusy || push === null) return;
    if (push !== 'on' && push !== 'off') return; // the four dead states have no control
    setPushBusy(true);
    setPushError(null);
    const status = push === 'on' ? await disablePush(tenantId) : await enablePush(tenantId);
    setPush(status.state);
    setPushError(status.error ?? null);
    setPushBusy(false);
  }

  /** OFF MEANS OFF. The chip renders the mode the SERVER holds and writes
   * the next one through; nothing is kept locally beyond the echo of what
   * was just written, so a failed PUT cannot leave the screen claiming a
   * setting that is not stored. */
  function cycleNotifyCategory(category: string) {
    const current = notifyModes[category] ?? NOTIFY_DEFAULT_MODE;
    const next = MODE_ORDER[(MODE_ORDER.indexOf(current) + 1) % MODE_ORDER.length]!;
    setNotifyModes((prev) => ({ ...prev, [category]: next }));
    void setNotificationPreference(tenantId, category, next).catch(() => {
      // Put the server's last known answer back rather than leave a chip
      // asserting a mode nobody stored.
      setNotifyModes((prev) => ({ ...prev, [category]: current }));
      setPushError('That preference didn’t save. Try again.');
    });
  }

  async function signOutSession(session: DeviceSession) {
    if (session.current) {
      // Revoking the session you are on IS logging out — say it by doing it.
      await logout().catch(() => {});
      window.location.reload();
      return;
    }
    await revokeDeviceSession(session.id).catch(() => {});
    setSessions((rows) => rows.filter((r) => r.id !== session.id));
  }

  const actorRoles = jobRoles;
  const tickets: FirstRunTicket[] = useMemo(() => {
    const raw = record?.fields.tickets;
    return Array.isArray(raw) ? (raw as FirstRunTicket[]) : [];
  }, [record]);

  function fieldText(name: string): string {
    const v = record?.fields[name];
    return typeof v === 'string' && v.trim() !== '' ? v : '—';
  }

  function recordRow(label: string, fieldName: string, small?: string) {
    return (
      <div class="stg__row">
        <span class="stg__k">
          {label}
          {small ? <small>{small}</small> : null}
        </span>
        <span class="stg__v">
          {fieldText(fieldName)} <OwnershipMark ownership={ownershipFromRule(RECORD_RULES[fieldName], actorRoles)} />
        </span>
      </div>
    );
  }

  /** ⚑ THE ONLY THING EITHER DOOR DOES IS LEAVE. Both answer with a URL on
   * Stripe's own domain — no card field is ever rendered here, which is what
   * keeps card data outside this product entirely. `location.assign` rather
   * than a new tab: a payment is the thing the person is doing, not a detour,
   * and Stripe returns them to this same screen. */
  // W1 — READ IT ONLY WHERE THE TIER INCLUDES IT (house rule 9: the client
  // reads `planHas` to NOT-OFFER; the server's requireFeature is the belt
  // behind that brace). A Free household never fires this, so it never meets
  // the 403 it would earn.
  //
  // ⚠ A FAILED READ LEAVES THE BLOCK ABSENT, not broken. On every deployment
  // without a mailbox broker the route 404s, and "no work address here" is the
  // truthful surface — the same absent-not-empty posture the inbound address
  // and the storage meter already take.
  useEffect(() => {
    if (!planHas(plan, 'email.outbound')) return;
    let cancelled = false;
    void getWorkAddress(tenantId)
      .then((w) => {
        if (!cancelled) setWorkAddress(w);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [tenantId, plan]);

  /** Provision or reset, one handler: the ritual is the same from both sides
   * and the only difference is which door. The credential goes into state
   * BEFORE the status is refreshed, so nothing can navigate the family past
   * the one moment the password exists. */
  async function runWorkAddressRitual(mode: 'provision' | 'reset'): Promise<void> {
    setWorkError(null);
    setWorkBusy(true);
    try {
      const result = mode === 'provision'
        ? await provisionWorkAddress(tenantId)
        : await resetWorkAddressPassword(tenantId);
      setWorkCredential(result);
      setWorkAddress({ mailbox: result.mailbox, slugAvailable: true });
    } catch (err) {
      // Shown, never swallowed: a family who taps and sees nothing happen
      // concludes the product is broken. The server's messages are already
      // written for a person to read.
      setWorkError(err instanceof Error ? err.message : 'that did not go through, and nothing was created');
    } finally {
      setWorkBusy(false);
    }
  }

  // P5 — READ IT FOR EVERY HOUSEHOLD ONCE THE PLAN IS KNOWN, and that is a
  // deliberate departure from the work-address read above.
  //
  // ⚠ THE LAPSE IS WHY. A household that connected a mailbox on Pro and then
  // dropped a tier still HAS a connected mailbox: we hold a credential to their
  // private inbox, reading has stopped, and they need to be told and to be able
  // to revoke it. Asking `planHas` first would hide exactly that case — the one
  // that matters most — so the read is ungated on the server (it can never
  // 403) and unconditional here. The cost is one cheap GET for a Free household
  // that has nothing connected; the block still draws nothing for them.
  useEffect(() => {
    if (!plan) return;
    let cancelled = false;
    void getByoMailbox(tenantId)
      .then((b) => {
        if (!cancelled) setByo(b);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [tenantId, plan]);

  /** ⚑ THE RETURN FROM THE HOSTED PAGE, READ ONCE AND THEN WIPED.
   *
   * Stripe sends a family back to `/?billing=success#/settings` (server
   * billing/config.ts: the outcome cannot ride inside the hash, or the
   * router parses a screen called `settings?billing=success`). Without this
   * they landed on an unchanged screen with nothing said — a payment that
   * looks like it did nothing is the one moment a family reaches for their
   * bank rather than the app.
   *
   * The param is removed from the URL immediately: a reload must not replay
   * a receipt for a payment that landed minutes ago.
   */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const outcome = params.get('billing');
    if (outcome !== 'success' && outcome !== 'cancelled' && outcome !== 'managed') return;
    setBillingReturn(outcome);
    params.delete('billing');
    const query = params.toString();
    window.history.replaceState(
      null,
      '',
      `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`
    );
    // Nothing moved on a cancel, so there is nothing to re-read.
    if (outcome === 'cancelled') return;
    // ⚠ THE WEBHOOK IS WHAT MOVES THE TIER, and it lands a beat after the
    // browser does. So the plan is re-read a few times and then left alone —
    // never a spinner, and never a permanent poll.
    let cancelled = false;
    let timer = 0;
    let attempts = 0;
    const poll = (): void => {
      attempts += 1;
      void getPlan(tenantId)
        .then((p) => {
          if (cancelled) return;
          setPlan(p);
          if (p.billing.status === 'none' && attempts < 3) timer = window.setTimeout(poll, 2500);
        })
        .catch(() => {});
    };
    timer = window.setTimeout(poll, 1500);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [tenantId]);

  useEffect(() => {
    let cancelled = false;
    void getIcsFeedStatus(tenantId)
      .then((st) => {
        if (!cancelled) setIcs(st);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  /** Mint (or replace) the feed link. The server revokes any prior token
   * first, so "New link" and first-time setup are the same act — and a
   * replaced link means every old copy out in the world stops working,
   * which is exactly what someone rotating a leaked link wants. */
  async function createIcsLink(): Promise<void> {
    setIcsError(null);
    setIcsBusy(true);
    setIcsCopied(false);
    try {
      const minted = await mintIcsFeed(tenantId);
      setIcsMint(minted);
      setIcs({ active: true, createdAt: new Date().toISOString(), lastUsedAt: null });
    } catch (err) {
      setIcsError(err instanceof Error ? err.message : 'the link could not be created');
    } finally {
      setIcsBusy(false);
    }
  }

  // The sync-mode law (server: calendar/invites.ts): feed and invites never
  // run together. Choosing invites revokes any live feed link HERE, visibly —
  // and the server refuses a mint while the mode is 'invites' regardless.
  const icsSyncMode = prefs['calendar_sync_mode'] === 'invites' ? 'invites' : 'feed';

  async function chooseInviteMode(): Promise<void> {
    setIcsError(null);
    savePref('calendar_sync_mode', 'invites');
    if (ics?.active || icsMint) {
      try {
        await revokeIcsFeed(tenantId);
      } catch {
        // The pref carries the law server-side either way; a failed revoke
        // leaves a token the server will still refuse to re-mint.
      }
      setIcsMint(null);
      setIcsCopied(false);
      setIcs({ active: false, createdAt: null, lastUsedAt: null });
    }
  }

  function chooseFeedMode(): void {
    setIcsError(null);
    savePref('calendar_sync_mode', 'feed');
  }

  async function turnOffIcs(): Promise<void> {
    setIcsError(null);
    setIcsBusy(true);
    try {
      await revokeIcsFeed(tenantId);
      setIcsMint(null);
      setIcsCopied(false);
      setIcs({ active: false, createdAt: null, lastUsedAt: null });
    } catch (err) {
      setIcsError(err instanceof Error ? err.message : 'sync could not be turned off');
    } finally {
      setIcsBusy(false);
    }
  }

  /** Connect the mailbox the family typed. The server PROVES the login before
   * it stores anything, so a refusal here means nothing was saved and the only
   * thing to do is fix what was typed — which is why the server's own sentence
   * is shown rather than a second copy written here. */
  async function connectByo(): Promise<void> {
    setByoError(null);
    setByoBusy(true);
    try {
      const result = await connectByoMailbox(tenantId, {
        address: byoDraft.address.trim(),
        imapHost: byoDraft.imapHost.trim(),
        // ⚑ THE FIELD'S OWN TEXT, NOT A GUESS AT IT (2026-09-16, the Day 3–4
        // walk, item 7). `Number(byoDraft.imapPort) || 993` read "notaport"
        // as 993 and then tried to CONNECT to it — so a malformed port fell
        // through to "we could not reach that mail server (connect)", a
        // later-stage failure about the wrong thing. The server already
        // refuses a bad port in its own sentence, and reads '' as its own
        // default; what it could never do was see a value this client had
        // already replaced.
        imapPort: byoDraft.imapPort.trim(),
        password: byoDraft.password,
      });
      setByo(result);
      // The credential is gone from this client the instant it is no longer
      // needed. It was never anywhere else.
      setByoDraft({ address: '', imapHost: '', imapPort: '993', password: '' });
    } catch (err) {
      setByoError(err instanceof Error ? err.message : 'that mailbox did not connect, and nothing was saved');
    } finally {
      setByoBusy(false);
    }
  }

  /** ⚑ DISCONNECT DESTROYS THE STORED CREDENTIAL. It does not touch a single
   * message that already arrived — those are the household's record and stay
   * theirs. The button's own copy says both halves. */
  async function disconnectByo(): Promise<void> {
    setByoError(null);
    setByoBusy(true);
    try {
      await disconnectByoMailbox(tenantId);
      setByo((prev) => (prev ? { ...prev, mailbox: null, polling: false, stoppedReason: null } : prev));
    } catch (err) {
      setByoError(err instanceof Error ? err.message : 'that did not go through — the mailbox is still connected');
    } finally {
      setByoBusy(false);
    }
  }

  useEffect(() => {
    const live = plan?.billing.interval;
    if (live && !intervalTouched) setBillingInterval(live);
  }, [plan, intervalTouched]);

  /* ── ⚑ WHICH BILLING DOOR THIS HOUSEHOLD HAS ──
     A LIVE subscription is one the change door will move: `active`,
     `trialing`, a card Stripe is still retrying (`past_due`) and one already
     told to end (`canceling`) are all subscriptions. `incomplete` never
     granted anything and `lapsed` is over — both of those buy again through
     Checkout, which is exactly what the server allows. */
  const subscribed =
    plan !== null &&
    (plan.billing.status === 'active' ||
      plan.billing.status === 'trialing' ||
      plan.billing.status === 'past_due' ||
      plan.billing.status === 'canceling');

  async function goToCheckout(tier: 'regular' | 'pro'): Promise<void> {
    setBillingError(null);
    setBillingBusy(tier);
    try {
      const { checkoutUrl } = await createBillingCheckout(tenantId, { tier, interval: billingInterval });
      window.location.assign(checkoutUrl);
    } catch (err) {
      setBillingBusy(null);
      setBillingError(err instanceof Error ? err.message : 'that did not go through, and nothing was charged');
    }
  }

  async function goToPortal(): Promise<void> {
    setBillingError(null);
    setBillingBusy('portal');
    try {
      const { portalUrl } = await openBillingPortal(tenantId);
      window.location.assign(portalUrl);
    } catch (err) {
      setBillingBusy(null);
      setBillingError(err instanceof Error ? err.message : 'that door would not open');
    }
  }

  /** ⚑ THE DOOR A PAYING HOUSEHOLD NEEDS, AND THE ONE CHECKOUT CANNOT BE.
   *
   * A second Checkout Session opens a SECOND subscription, so the Buy door
   * now refuses one (409 `subscription_exists`) — which left every paying
   * family with an Upgrade button that could only fail. Tier, interval, or
   * both move on the subscription that already exists, in either direction,
   * and the server's own refusal is what gets shown when it will not move.
   */
  async function switchPlan(tier: 'regular' | 'pro'): Promise<void> {
    setBillingError(null);
    setBillingBusy(tier);
    try {
      await changeBillingPlan(tenantId, { tier, interval: billingInterval });
      setPlan(await getPlan(tenantId));
    } catch (err) {
      setBillingError(
        err instanceof Error ? err.message : 'that change did not go through, and nothing was charged'
      );
    } finally {
      setBillingBusy(null);
    }
  }

  /** One more block of room. Through the change door where a subscription
   * exists and through Checkout where it does not — the same split the two
   * routes enforce on each other. The SIZE of a block is a platform lever and
   * the price is Stripe's; neither is quoted here, because a second copy of
   * either is a number that drifts. */
  async function buyRoom(): Promise<void> {
    if (!plan) return;
    const tier = plan.key === 'pro' ? 'pro' : 'regular';
    const addonUnits = plan.billing.addonUnits + 1;
    setBillingError(null);
    setBillingBusy('addon');
    try {
      if (subscribed) {
        await changeBillingPlan(tenantId, {
          tier,
          interval: plan.billing.interval ?? billingInterval,
          addonUnits,
        });
        setPlan(await getPlan(tenantId));
        setBillingBusy(null);
      } else {
        const { checkoutUrl } = await createBillingCheckout(tenantId, {
          tier,
          interval: billingInterval,
          addonUnits,
        });
        window.location.assign(checkoutUrl);
      }
    } catch (err) {
      setBillingBusy(null);
      setBillingError(
        err instanceof Error ? err.message : 'that did not go through, and nothing was charged'
      );
    }
  }

  return (
    <div class="stg">
      <BackLink label="Today" onClick={onBack} />
      <h1 class="stg__title">Settings</h1>

      {/* ⚑ WHAT THE HOSTED PAGE JUST DID, SAID HERE. It is rendered above the
          columns rather than inside the Plan section on purpose: a family
          coming back from Stripe must be told even on the beat before the
          plan has been re-read, and even if that read fails. */}
      {billingReturn ? (
        <p class="stg__receipt" role="status" data-testid="stg-billing-return">
          {billingReturn === 'success'
            ? 'Payment received — your plan updates here in a moment.'
            : billingReturn === 'cancelled'
              ? 'Nothing was charged — the payment page was closed before it finished.'
              : 'Back from Stripe — the plan below is what is on file now.'}
        </p>
      ) : null}

      <div class={`stg__cols${entry?.settings?.memberRecord ? '' : ' stg__cols--one'}`}>
        {/* ------------------------------- yours ------------------------ */}
        <div class="stg__col">
          <div class="stg__sec">
            <span>Preferences</span>
            <span class="stg__mini">yours</span>
          </div>
          {/* ⚑ FIRST IN THE COLUMN, because it is the one thing here a new
              household arrives already needing. Without it the app greets them
              by their email local part on Today and on every capture. */}
          <div class="stg__row">
            <span class="stg__k">
              <label for="stg-name">Your name</label>
              <small>{nameSaved ? 'saved' : 'how the app greets you'}</small>
            </span>
            <span class="stg__v">
              <input
                id="stg-name"
                class="stg__input"
                type="text"
                autocomplete="name"
                value={nameDraft}
                disabled={nameBusy}
                placeholder={me?.email?.split('@')[0] ?? ''}
                onInput={(e) => {
                  setNameDraft((e.target as HTMLInputElement).value);
                  setNameSaved(false);
                }}
              />
              <button
                type="button"
                class="stg__chip"
                disabled={nameBusy || nameDraft.trim().length === 0 || nameDraft.trim() === (me?.name ?? '')}
                onClick={() => {
                  const next = nameDraft.trim();
                  if (!next) return;
                  setNameBusy(true);
                  void updateMyName(next)
                    .then((user) => {
                      // Reflect the SERVER's answer, not the draft — it is the
                      // one that decides what every other screen renders.
                      setMe((prev) => (prev ? { ...prev, name: user.name ?? next } : prev));
                      setNameDraft(user.name ?? next);
                      onUserUpdated?.(user);
                      setNameSaved(true);
                    })
                    .catch(() => setNameSaved(false))
                    .finally(() => setNameBusy(false));
                }}
              >
                Save
              </button>
            </span>
          </div>
          <div class="stg__row">
            <span class="stg__k">
              Hold to talk<small>Off means tap, then speak</small>
            </span>
            <button
              type="button"
              class="stg__toggle"
              aria-pressed={prefValue('hold_to_talk')}
              aria-label="Hold to talk"
              onClick={() => savePref('hold_to_talk', !prefValue('hold_to_talk'))}
            />
          </div>
          <div class="stg__row">
            <span class="stg__k">
              Confirm before filing<small>Slower, but you see it first</small>
            </span>
            <button
              type="button"
              class="stg__toggle"
              aria-pressed={prefValue('confirm_before_filing')}
              aria-label="Confirm before filing"
              onClick={() => savePref('confirm_before_filing', !prefValue('confirm_before_filing'))}
            />
          </div>
          <div class="stg__row">
            <span class="stg__k">
              Appearance<small>Follows your phone</small>
            </span>
            <button type="button" class="stg__chip" onClick={cycleAppearance}>
              {APPEARANCE_WORD[prefValue('appearance')]}
            </button>
          </div>

          {/* ------------------- CV6b §3: notifications ------------------ */}
          <div class="stg__sec stg__sec--mt">
            <span>Notifications</span>
            <span class="stg__mini">yours</span>
          </div>
          {push !== null ? (
            <div class="stg__row">
              <span class="stg__k">
                This phone
                <small>{PUSH_WORD[push]}</small>
              </span>
              {push === 'on' || push === 'off' ? (
                <button
                  type="button"
                  class="stg__toggle"
                  aria-pressed={push === 'on'}
                  aria-label="Notifications on this phone"
                  disabled={pushBusy}
                  data-testid="stg-push-toggle"
                  onClick={() => void togglePush()}
                />
              ) : (
                // NO DEAD BUTTON. Four states cannot produce a subscription;
                // the sub-line above already said which one this is, and
                // there is nothing here to press.
                <span class="stg__v stg__v--mute" data-testid="stg-push-state">
                  —
                </span>
              )}
            </div>
          ) : null}
          {push === 'on' && entry?.settings?.pushFallbackNote ? (
            // DECISION 0d25040b, ruling 3 (Huan, 2026-08-16): the Telegram
            // double-ping is handled by SAYING it, not building it. A tech
            // keeps FULL Telegram access (8a36f2de), so the shop group goes
            // on buzzing for every job in the shop, and no server of ours
            // can scope somebody else's Telegram. Saying so IS the whole
            // deliverable of that ruling.
            //
            // It lives HERE and not in the first-run step, which was the
            // obvious home until a test proved otherwise: turning pings on
            // passes that step in the same beat, so its 'on' state is never
            // a page anybody reads. This row renders for as long as pings
            // are on, which is what "say it" actually requires.
            //
            // ⚠ AND ONLY WHERE THE PACK DECLARED ONE. It named Rexx and a
            // shop group on every build until 2026-09-01, including the
            // household one, where there is no shop and no group.
            <div class="stg__note" data-testid="stg-push-telegram">
              {entry.settings.pushFallbackNote}
            </div>
          ) : null}
          {push === 'blocked' ? (
            // THE WEB CANNOT RE-ASK. Where the switch actually lives is the
            // only remaining true and useful thing to say.
            <div class="stg__note" data-testid="stg-push-blocked">
              Turn them back on in your phone’s settings — nothing here can.
            </div>
          ) : null}
          {pushError ? (
            <div class="stg__note" role="status" data-testid="stg-push-error">
              {pushError}
            </div>
          ) : null}
          {NOTIFY_KEYS.map((key) => {
            const copy = entry?.settings?.notify?.[key] ?? NOTIFY_NEUTRAL[key];
            return (
              <div class="stg__row" key={key}>
                <span class="stg__k">
                  {copy.label}
                  <small>{copy.sub}</small>
                </span>
                <button
                  type="button"
                  class="stg__chip"
                  data-testid={`stg-notify-${key}`}
                  onClick={() => cycleNotifyCategory(key)}
                >
                  {MODE_WORD[notifyModes[key] ?? NOTIFY_DEFAULT_MODE]}
                </button>
              </div>
            );
          })}

          <div class="stg__sec stg__sec--mt">
            <span>Connections</span>
            <span class="stg__mini">yours</span>
          </div>
          {lanes.map((lane) => (
            <div key={lane.key} class="stg__row">
              <span class="stg__k">
                {lane.label}
                <small>Telegram</small>
              </span>
              <span class={`stg__v${lane.connected ? ' stg__v--good' : ''}`}>
                {lane.connected ? 'Connected' : selfEnrol?.phoneMasked ? 'Finish on your phone' : 'Waiting'}
              </span>
            </div>
          ))}
          {/* THE ENROLMENT DOOR (Huan, 2026-08-25). Two facts decide what
              this shows, and both come from the server:

              No number yet → the field. Typing a number is just typing, so
              it works at a desk as well as anywhere else.

              A number, not yet bound → name the phone. Telegram's
              share-contact button is MOBILE-ONLY (it renders and does
              nothing on Desktop — verified live), so the bind can only
              happen there. The lane rows re-read on the next visit, so
              finishing on the phone flips them with nothing to tap here. */}
          {selfEnrol?.available && !lanes.every((l) => l.connected) ? (
            selfEnrol.phoneMasked ? (
              <div class="stg__row">
                <span class="stg__k">
                  {selfEnrol.phoneMasked}
                  <small>open either bot on that phone and share your number</small>
                </span>
              </div>
            ) : (
              <div class="stg__row">
                <span class="stg__k">
                  <label for="stg-enrol">Your mobile</label>
                  <small>{enrolError ?? 'then open either bot on that phone'}</small>
                </span>
                <span class="stg__v">
                  <input
                    id="stg-enrol"
                    class="stg__input"
                    type="tel"
                    autocomplete="tel"
                    value={enrolPhone}
                    disabled={enrolBusy}
                    onInput={(e) => setEnrolPhone((e.target as HTMLInputElement).value)}
                  />
                  <button
                    type="button"
                    class="stg__chip"
                    disabled={enrolBusy || !enrolPhone.trim()}
                    onClick={() => {
                      const typed = enrolPhone.trim();
                      if (!typed || enrolBusy || !selfEnrol) return;
                      setEnrolBusy(true);
                      setEnrolError(null);
                      void enrolMemberTelegram(tenantId, selfEnrol.membershipId, typed)
                        .then(() => getFirstRun(tenantId))
                        .then((st) => {
                          setLanes(st.lanes);
                          setSelfEnrol(st.selfEnrol);
                          setEnrolPhone('');
                        })
                        // The route's own words when it refuses the NUMBER
                        // (E.164 is required — a guessed country code would
                        // be an invented phone). Swallowing them leaves
                        // somebody retyping the same thing; FirstRun's own
                        // enrolment step already shows them.
                        .catch((err: unknown) =>
                          setEnrolError(
                            err instanceof ApiError && err.status === 400
                              ? err.message
                              : 'That didn’t go through. Try again.'
                          )
                        )
                        .finally(() => setEnrolBusy(false));
                    }}
                  >
                    Connect
                  </button>
                </span>
              </div>
            )
          ) : null}
          {sessions.map((s) => (
            <div key={s.id} class="stg__row">
              <span class="stg__k">
                {s.current ? 'This device' : deviceLabel(s.userAgent)}
                <small>
                  {s.current ? `Since ${shortDate(s.createdAt)}` : `Last used ${shortDate(s.lastSeenAt)}`}
                </small>
              </span>
              <button type="button" class="stg__chip" onClick={() => void signOutSession(s)}>
                Sign out
              </button>
            </div>
          ))}

          {/* SET UP AGAIN (Huan, 2026-08-25). The walk holds steps whose
              answer belongs to a DEVICE and a MOMENT rather than to a
              person: "add this to your home screen" is answered on whatever
              phone is in your hand, and a licence expires. Someone who set
              the account up at a desk, or changed phones, or let a ticket
              lapse, had no way back — completion is stamped once and nothing
              could clear it. Only the steps that can honestly be re-answered
              are offered; identity is not among them. */}
          {REOPENABLE_STEPS.some((st) => walkSteps.includes(st.key)) ? (
            <>
              <div class="stg__sec stg__sec--mt">
                <span>Set up again</span>
                <span class="stg__mini">on this device</span>
              </div>
              {REOPENABLE_STEPS.filter((st) => walkSteps.includes(st.key)).map((st) => (
                <div key={st.key} class="stg__row">
                  <span class="stg__k">
                    {st.label}
                    <small>{st.sub}</small>
                  </span>
                  <button
                    type="button"
                    class="stg__chip"
                    disabled={reopening !== null}
                    onClick={() => {
                      if (reopening) return;
                      setReopening(st.key);
                      void reopenFirstRunStep(tenantId, st.key)
                        // The shell re-reads the walk on mount and renders it
                        // when `resume` is not null, so this IS the door.
                        .then(() => window.location.reload())
                        .catch(() => setReopening(null));
                    }}
                  >
                    {reopening === st.key ? 'Opening…' : 'Set up'}
                  </button>
                </div>
              ))}
            </>
          ) : null}

          {/* THE OFFICE. Admin and dispatch are invited here rather than from
              Techs, which is the crew's screen. Split on the pack's declared
              SEAT, never on role names: whoever this pack seats ABOVE its
              members is office, and a pack that renames its roles or adds one
              gets this for free. `owner` is excluded — a second owner is not
              a thing you do from a preferences panel.

              ⚑ AMENDED 2026-09-11 (G-18). Splitting on the SEAT is right for
              a trade deployment and was wrong for a household, where the one
              job role that could not be invited — `helper` — is seated AT
              member on purpose. A pack that names its own seats is now asked
              which it offers; the tier split below is what everyone else
              still gets. */}
          {/* ⚑ ASK THE AUTHORITY, THEN THE RANK — never the rank alone
              (2026-09-15). Where the pack names the action this door spends,
              its answer is the whole gate; where it names none, the rank is
              still all there is to ask. */}
          {me && (mayInvite ?? canEditTechs(me)) && canInviteSomebody ? (
            <>
              <div class="stg__sec stg__sec--mt">
                <span>{(entry?.settings?.invite ?? INVITE_NEUTRAL).section}</span>
                <span class="stg__mini">
                  {entry?.settings?.invite?.mini ?? INVITE_NEUTRAL.mini}
                </span>
              </div>
              {/* ⚑ G-18 — ONE ROW PER SEAT THE PACK DECLARES. A household
                  seats two kinds of person beside its performers — the second
                  adult and the helper — and a single row could only ever
                  offer the first. Each row carries the pack's own words and
                  opens the panel already knowing which seat it is for; the
                  question is answered once, on the row you pressed.

                  A pack that declares no seats keeps the single row it always
                  had, opening the panel with its own job-role picker. */}
              {declaredSeats
                ? declaredSeats.map((s) => (
                    <div class="stg__row" key={s.jobRole}>
                      <span class="stg__k">
                        {s.label}
                        {s.sub ? <small>{s.sub}</small> : null}
                      </span>
                      <button
                        type="button"
                        class="stg__chip"
                        data-testid={`invite-seat-${s.jobRole}`}
                        onClick={() => setInviteOpen(s)}
                      >
                        Invite
                      </button>
                    </div>
                  ))
                : (
                    <div class="stg__row">
                      <span class="stg__k">
                        {(entry?.settings?.invite ?? INVITE_NEUTRAL).label ?? INVITE_NEUTRAL.label}
                        {entry?.settings?.invite?.sub ? <small>{entry.settings.invite.sub}</small> : null}
                      </span>
                      <button type="button" class="stg__chip" onClick={() => setInviteOpen(true)}>
                        Invite
                      </button>
                    </div>
                  )}
            </>
          ) : null}

          {/* ── the household's own inbound address ──
              The Free tier promises "the career's own address — mail in,
              auto-collected". The receiving half has existed since P4.3; what
              was missing was that nothing ever TOLD a family what the address
              is, which makes a built capability unusable.

              ⚑ ABSENT, NOT EMPTY, when there is no address. `null` means
              inbound mail is not wired on this deployment or the household has
              no slug — and an absent row is the honest surface. A placeholder
              here would be an address a parent gives to a casting director. */}
          {mailbox ? (
            <>
              <div class="stg__sec stg__sec--mt">
                <span>Mail it in</span>
                <span class="stg__mini">forwards straight into the record</span>
              </div>
              <div class="stg__row">
                <span class="stg__k">
                  Your address
                  <small>{mailbox}</small>
                </span>
                <button
                  type="button"
                  class="stg__chip"
                  onClick={() => void navigator.clipboard?.writeText(mailbox)}
                >
                  Copy
                </button>
              </div>
            </>
          ) : null}

          {/* ── ⚑ W1 — THE WORK ADDRESS (decision `80350c67`) ──

              NOT the row above. That one is the household's free INBOUND
              handle — a forwarding route, not a mailbox. This is the real
              account on the mail host that a parent can put on a business
              card and that the app SENDS FROM when a draft is approved.

              ⚠ THE WHOLE BLOCK IS ABSENT BELOW WORKING, and absent again where
              this deployment has no mailbox broker (the read 404s and
              `workAddress` stays null). Absent, not disabled: a door drawn and
              then refused reads as broken rather than as an upsell, which is
              billing's own argument one section down. The tier list on
              calltowrap.com is where the promise is made; a locked control
              here would make it twice, worse.

              ⚠ AND WHERE THE HOUSEHOLD HAS NO HANDLE (`slugAvailable` false)
              the door is absent too, because pressing it could only ever
              fail — `996b486`: a name that folds to nothing gets NO slug
              rather than an invented one. */}
          {planHas(plan, 'email.outbound') && workAddress ? (
            <>
              <div class="stg__sec stg__sec--mt">
                <span>Your work address</span>
                <span class="stg__mini">mail out, as you</span>
              </div>

              {workAddress.mailbox ? (
                <>
                  <div class="stg__row">
                    <span class="stg__k" data-testid="stg-work-address">
                      {workAddress.mailbox.address}
                      <small>
                        IMAP {workAddress.mailbox.imapHost}:{workAddress.mailbox.imapPort} · SMTP{' '}
                        {workAddress.mailbox.smtpHost}:{workAddress.mailbox.smtpPort}
                      </small>
                    </span>
                    <button
                      type="button"
                      class="stg__chip"
                      onClick={() => void navigator.clipboard?.writeText(workAddress.mailbox!.address)}
                    >
                      Copy
                    </button>
                  </div>

                  {/* ⚑ THE REFUSED-CREDENTIAL STATE, IN WORDS. The mail host
                      rejected the stored password, so approvals are not
                      sending — and the family would otherwise have no way to
                      know that. The server's own sentence, not a second copy
                      of it written here. */}
                  {workAddress.mailbox.state === 'unusable' ? (
                    <p class="stg__note" data-testid="stg-work-address-unusable">
                      {workAddress.mailbox.stateReason ?? 'This address is not sending right now.'}
                    </p>
                  ) : null}

                  <div class="stg__row">
                    <span class="stg__k">
                      Password
                      <small>
                        {workAddress.mailbox.lastResetAt
                          ? `Last changed ${shortDate(workAddress.mailbox.lastResetAt)}`
                          : `Set up ${shortDate(workAddress.mailbox.provisionedAt)}`}
                      </small>
                    </span>
                    <button
                      type="button"
                      class="stg__chip"
                      data-testid="stg-work-address-reset"
                      disabled={workBusy}
                      onClick={() => void runWorkAddressRitual('reset')}
                    >
                      {workBusy ? 'Working…' : 'New one'}
                    </button>
                  </div>
                </>
              ) : workAddress.slugAvailable ? (
                <div class="stg__row">
                  <span class="stg__k">A mailbox of your own</span>
                  <button
                    type="button"
                    class="stg__chip"
                    data-testid="stg-work-address-create"
                    disabled={workBusy}
                    onClick={() => void runWorkAddressRitual('provision')}
                  >
                    {workBusy ? 'Making it…' : 'Make it'}
                  </button>
                </div>
              ) : null}

              {/* ⚑ THE ONE CROSSING. This is the only moment the password
                  exists anywhere but the mail host and an encrypted column —
                  there is no verb that can show it again, and the sentence
                  says so rather than letting a family find out by asking. */}
              {workCredential ? (
                <p class="stg__note" data-testid="stg-work-address-password">
                  <strong>{workCredential.password}</strong>
                  <br />
                  Shown once. Put it in your mail app now — a new one can be made, but this one
                  cannot be shown again.
                </p>
              ) : null}

              {workError ? (
                <p class="stg__note" data-testid="stg-work-address-error">
                  {workError}
                </p>
              ) : null}
            </>
          ) : null}

          {/* ── ⚑ P5 — THE MAILBOX THEY ALREADY HAD (plan model §6) ──

              The third address block on this screen, and none of the three is
              either of the others. The first is the free inbound handle. The
              second is the work address the app SENDS from. This one is an
              account on a family's OWN provider that the product only ever
              READS — so the agents who have been mailing that address for six
              years can carry on, and it all files anyway.

              ⚠ THE BLOCK IS ABSENT unless the household can connect one (Pro)
              or already has. A Free household with nothing connected sees
              nothing — absent, not disabled, for the same reason the work
              address is: a drawn door that then refuses reads as broken rather
              than as an upsell, and the tier list on calltowrap.com is where
              the promise gets made.

              ⚑ AND A LAPSED HOUSEHOLD STILL SEES IT, deliberately. They have a
              connected mailbox, we hold a credential to it, and reading has
              stopped — all three of those are facts they are owed, and the
              Disconnect button must survive a plan change or a revoked
              credential has nowhere to go. */}
          {byo && (byo.connectable || byo.mailbox) ? (
            <>
              <div class="stg__sec stg__sec--mt">
                <span>Your own mailbox</span>
                <span class="stg__mini">mail in</span>
              </div>

              {byo.mailbox ? (
                <>
                  <div class="stg__row">
                    <span class="stg__k" data-testid="stg-byo-address">
                      {byo.mailbox.address}
                      <small>
                        IMAP {byo.mailbox.imapHost}:{byo.mailbox.imapPort} · your inbox, from the day you
                        connected
                      </small>
                    </span>
                    <button
                      type="button"
                      class="stg__chip"
                      data-testid="stg-byo-disconnect"
                      disabled={byoBusy}
                      onClick={() => void disconnectByo()}
                    >
                      {byoBusy ? 'Working…' : 'Disconnect'}
                    </button>
                  </div>

                  {/* ⚑ THE STOPPED STATE, IN WORDS. Reading can stop for three
                      different reasons — the plan changed, the provider refused
                      the password, or nothing is connected — and each needs a
                      different thing from a person. The server's own sentence,
                      not a second copy written here. */}
                  {byo.polling ? (
                    <div class="stg__row">
                      <span class="stg__k" data-testid="stg-byo-reading">
                        Reading
                        <small>
                          {byo.mailbox.lastPolledAt
                            ? `Last looked ${shortDate(byo.mailbox.lastPolledAt)} · ${byo.mailbox.messagesIngested} in so far`
                            : `Connected ${shortDate(byo.mailbox.connectedAt)}`}
                        </small>
                      </span>
                    </div>
                  ) : (
                    <p class="stg__note" data-testid="stg-byo-stopped">
                      {byo.stoppedReason ?? 'This mailbox is not being read right now.'}
                    </p>
                  )}

                  {byo.polling && byo.mailbox.lastError ? (
                    <p class="stg__note" data-testid="stg-byo-last-error">
                      {byo.mailbox.lastError}
                    </p>
                  ) : null}
                </>
              ) : (
                <>
                  <div class="stg__row">
                    <span class="stg__k">
                      <label for="stg-byo-address-in">Mailbox</label>
                      <small>the address your agents already write to</small>
                    </span>
                    <span class="stg__v">
                      <input
                        id="stg-byo-address-in"
                        class="stg__input"
                        type="email"
                        autocomplete="email"
                        value={byoDraft.address}
                        disabled={byoBusy}
                        onInput={(e) =>
                          setByoDraft((d) => ({ ...d, address: (e.target as HTMLInputElement).value }))
                        }
                      />
                    </span>
                  </div>
                  <div class="stg__row">
                    <span class="stg__k">
                      <label for="stg-byo-host-in">Mail server</label>
                      <small>your provider's IMAP host</small>
                    </span>
                    <span class="stg__v">
                      <input
                        id="stg-byo-host-in"
                        class="stg__input"
                        type="text"
                        placeholder="imap.example.com"
                        value={byoDraft.imapHost}
                        disabled={byoBusy}
                        onInput={(e) =>
                          setByoDraft((d) => ({ ...d, imapHost: (e.target as HTMLInputElement).value }))
                        }
                      />
                      <input
                        id="stg-byo-port-in"
                        class="stg__input"
                        type="text"
                        inputMode="numeric"
                        value={byoDraft.imapPort}
                        disabled={byoBusy}
                        onInput={(e) =>
                          setByoDraft((d) => ({ ...d, imapPort: (e.target as HTMLInputElement).value }))
                        }
                      />
                    </span>
                  </div>
                  <div class="stg__row">
                    <span class="stg__k">
                      {/* ⚠ "App password" is not an instruction, it is the NAME
                          of the thing every major provider issues for this —
                          and the one word that saves a family from typing the
                          password they sign in with and being refused. */}
                      <label for="stg-byo-password-in">App password</label>
                      <small>not the one you sign in with</small>
                    </span>
                    <span class="stg__v">
                      <input
                        id="stg-byo-password-in"
                        class="stg__input"
                        type="password"
                        autocomplete="off"
                        value={byoDraft.password}
                        disabled={byoBusy}
                        onInput={(e) =>
                          setByoDraft((d) => ({ ...d, password: (e.target as HTMLInputElement).value }))
                        }
                      />
                      <button
                        type="button"
                        class="stg__chip"
                        data-testid="stg-byo-connect"
                        disabled={byoBusy || !byoDraft.address.trim() || !byoDraft.imapHost.trim() || !byoDraft.password}
                        onClick={() => void connectByo()}
                      >
                        {byoBusy ? 'Checking…' : 'Connect'}
                      </button>
                    </span>
                  </div>
                </>
              )}

              {byoError ? (
                <p class="stg__note" data-testid="stg-byo-error">
                  {byoError}
                </p>
              ) : null}
            </>
          ) : null}

          {/* ── the storage meter (F1) ──
              Storage is THE meter of this product (decision a2bc8286). The
              cap shown is the EFFECTIVE one — performer multiplier and any
              add-on included — read from the same resolution that refuses an
              over-quota upload, so this number and a refusal can never
              disagree. Absent until the plan loads; never a guessed bar. */}
          {plan ? (
            <>
              <div class="stg__sec stg__sec--mt">
                <span>Storage</span>
                <span class="stg__mini">{plan.identity}</span>
              </div>
              <div class="stg__row">
                <span class="stg__k">
                  {formatGb(plan.storage.usedBytes)} of {formatGb(plan.storage.capBytes)} used
                  <small>
                    <span
                      class={`stg__meter${storageShare(plan.storage.usedBytes, plan.storage.capBytes) >= 0.9 ? ' stg__meter--near' : ''}`}
                    >
                      <span
                        class="stg__meter-fill"
                        style={{ width: `${Math.min(100, Math.round(storageShare(plan.storage.usedBytes, plan.storage.capBytes) * 100))}%` }}
                      />
                    </span>
                    {/* ⚑ THE MEANING IS IN THE WORD, NOT THE COLOUR. A bar
                        that turns red says nothing to a colour-blind reader
                        and nothing at all in a screen reader. */}
                    {storageShare(plan.storage.usedBytes, plan.storage.capBytes) >= 0.9 ? (
                      <span class="stg__meter-word" data-testid="stg-storage-near">
                        nearly full
                      </span>
                    ) : null}
                  </small>
                </span>
              </div>
            </>
          ) : null}

          {/* ── ⚑ B1 — WHAT THIS HOUSEHOLD PAYS, AND THE TWO DOORS ──

              Framework §5's lapse posture is SOFT + LOUD: the state line is
              always here when there is a subscription, and it says the state
              in words rather than colouring something and hoping.

              ⚠ THE WHOLE BLOCK IS ABSENT WHERE BILLING IS NOT CONFIGURED
              (`billing.available` is false — which is every deployment until
              the keys are minted). Absent, not disabled: a door drawn and then
              refused reads as broken rather than as an upsell, which is this
              product's own rule about tier segregation applied to the tier
              you would be buying.

              ⚠ AND THE UPGRADE DOOR IS THE ONE F3's RETENTION CHOOSER POINTS
              AT. App.tsx wires `onUpgrade` to this screen with a comment
              saying so; this is what makes that comment true. */}
          {plan && plan.billing.available ? (
            <>
              <div class="stg__sec stg__sec--mt">
                <span>Plan</span>
                <span class="stg__mini">{plan.identity}</span>
              </div>

              {plan.billing.status !== 'none' ? (
                <div class="stg__row">
                  <span class="stg__k" data-testid="stg-billing-state">
                    {plan.billing.status === 'past_due'
                      ? 'A payment did not go through'
                      : plan.billing.status === 'canceling'
                        ? 'Ending, and yours until it does'
                        : plan.billing.status === 'lapsed'
                          ? 'This plan has ended'
                          : plan.billing.status === 'incomplete'
                            ? 'This purchase was never finished'
                            : plan.billing.interval === 'year'
                              ? 'Billed yearly'
                              : 'Billed monthly'}
                    {plan.billing.currentPeriodEnd ? (
                      <small>
                        {plan.billing.status === 'past_due'
                          ? `Kept until ${shortDate(plan.billing.currentPeriodEnd)}`
                          : plan.billing.cancelAtPeriodEnd
                            ? `Until ${shortDate(plan.billing.currentPeriodEnd)}`
                            : `Renews ${shortDate(plan.billing.currentPeriodEnd)}`}
                      </small>
                    ) : null}
                  </span>
                  {plan.billing.manageable ? (
                    <button
                      type="button"
                      class="stg__chip"
                      data-testid="stg-billing-manage"
                      disabled={billingBusy !== null}
                      onClick={() => void goToPortal()}
                    >
                      Manage
                    </button>
                  ) : null}
                </div>
              ) : null}

              {/* ── EXTRA ROOM (`2f5ee92e`) ──
                  What was bought, and the door to buy another block where the
                  tier includes one. ⚠ NO SIZE AND NO PRICE ARE TYPED HERE:
                  the block's size is a platform lever
                  (`plan.storage_addon_unit_bytes`) and the amount is Stripe's
                  own on the hosted page. `addonBytes` below is the SERVER's
                  arithmetic, never this screen's. */}
              {plan.billing.addonUnits > 0 || plan.billing.canBuyAddon ? (
                <div class="stg__row">
                  <span class="stg__k">
                    Extra room
                    <small>
                      {plan.billing.addonUnits > 0
                        ? `${formatGb(plan.billing.addonBytes)} bought`
                        : plan.billing.addonUnitBytes
                          ? `${formatGb(plan.billing.addonUnitBytes)} at a time, added to what you already pay`
                          : 'more storage, added to what you already pay'}
                    </small>
                  </span>
                  {plan.billing.canBuyAddon ? (
                    <button
                      type="button"
                      class="stg__chip"
                      data-testid="stg-billing-addon"
                      disabled={billingBusy !== null}
                      onClick={() => void buyRoom()}
                    >
                      {billingBusy === 'addon' ? 'Working…' : plan.billing.addonUnits > 0 ? 'Add more' : 'Add'}
                    </button>
                  ) : null}
                </div>
              ) : null}

              {/* ── ⚑ THE TIER DOORS, AND WHICH ONE A HOUSEHOLD ACTUALLY HAS ──

                  A household with no live subscription BUYS: Checkout, the
                  tiers above the one they are on, "Choose".

                  A household with one SWITCHES, and switches in BOTH
                  directions. Checkout refuses them now (409
                  `subscription_exists` — one subscription per household,
                  structurally), so an Upgrade button wired to it could only
                  ever fail; and a family who wants to come DOWN a tier had no
                  door at all except cancelling outright. The interval is the
                  same act: the chip picks it, and the tier they are already on
                  offers a switch precisely when the picked interval differs
                  from the one they pay.

                  ⚠ NEVER A PRICE TYPED HERE. The amount a family is charged is
                  Stripe's own, shown on the hosted page before anything is
                  taken; a second copy of it in this file is a number that
                  drifts. */}
              {subscribed ? (
                <>
                  <div class="stg__row">
                    <span class="stg__k">Billing</span>
                    <button
                      type="button"
                      class="stg__chip"
                      data-testid="stg-billing-interval"
                      onClick={() => {
                        setIntervalTouched(true);
                        setBillingInterval(billingInterval === 'month' ? 'year' : 'month');
                      }}
                    >
                      {billingInterval === 'month' ? 'Monthly' : 'Yearly'}
                    </button>
                  </div>
                  {plan.billing.isFoundingInvitee ? (
                    // `e3e6b965` — the founding lock is a WORKING-tier promise,
                    // so it is said beside the tier doors. The RATE is not
                    // repeated here: it lives in the Stripe coupon this
                    // deployment is configured with, and a second copy of a
                    // price is a number that drifts.
                    <p class="stg__note" data-testid="stg-billing-founding">
                      Your founding rate applies to Working, and it does not expire.
                    </p>
                  ) : null}
                  {(['regular', 'pro'] as const)
                    .filter((tier) => tier !== plan.key || billingInterval !== plan.billing.interval)
                    .map((tier) => (
                      <div class="stg__row" key={tier}>
                        <span class="stg__k">
                          {tier === 'pro' ? 'Pro' : 'Working'}
                          {tier === plan.key ? (
                            <small>billed {billingInterval === 'year' ? 'yearly' : 'monthly'} instead</small>
                          ) : null}
                        </span>
                        <button
                          type="button"
                          class="stg__chip"
                          data-testid={`stg-billing-switch-${tier}`}
                          disabled={billingBusy !== null}
                          onClick={() => void switchPlan(tier)}
                        >
                          {billingBusy === tier ? 'Moving…' : 'Switch'}
                        </button>
                      </div>
                    ))}
                </>
              ) : plan.key !== 'pro' ? (
                <>
                  <div class="stg__row">
                    <span class="stg__k">Billing</span>
                    <button
                      type="button"
                      class="stg__chip"
                      data-testid="stg-billing-interval"
                      onClick={() => {
                        setIntervalTouched(true);
                        setBillingInterval(billingInterval === 'month' ? 'year' : 'month');
                      }}
                    >
                      {billingInterval === 'month' ? 'Monthly' : 'Yearly'}
                    </button>
                  </div>
                  {plan.billing.isFoundingInvitee ? (
                    <p class="stg__note" data-testid="stg-billing-founding">
                      Your founding rate applies to Working, and it does not expire.
                    </p>
                  ) : null}
                  {plan.key !== 'regular' ? (
                    <div class="stg__row">
                      <span class="stg__k">Working</span>
                      <button
                        type="button"
                        class="stg__chip"
                        data-testid="stg-billing-upgrade-regular"
                        disabled={billingBusy !== null}
                        onClick={() => void goToCheckout('regular')}
                      >
                        {billingBusy === 'regular' ? 'Opening…' : 'Choose'}
                      </button>
                    </div>
                  ) : null}
                  <div class="stg__row">
                    <span class="stg__k">Pro</span>
                    <button
                      type="button"
                      class="stg__chip"
                      data-testid="stg-billing-upgrade-pro"
                      disabled={billingBusy !== null}
                      onClick={() => void goToCheckout('pro')}
                    >
                      {billingBusy === 'pro' ? 'Opening…' : 'Choose'}
                    </button>
                  </div>
                </>
              ) : null}

              {billingError ? (
                <div class="stg__note" role="status" data-testid="stg-billing-error">
                  {billingError}
                </div>
              ) : null}
            </>
          ) : null}

          {/* Password lives HERE now, beside sign-out and the connections —
              one place for the account, as asked. The FORM is Account's own,
              imported rather than copied, so there is one of it.

              ⚠ AND IT IS ABSENT WHERE THERE IS NO PASSWORD TO CHANGE. C6a's
              front door is a magic link and then a passkey; nothing on this
              build mints a password, so a household HAS none and the form
              could only ever answer "Current password is incorrect". Only a
              deployment whose pack declares `passwordSignIn` draws it. */}
          <AccountPasswordSection email={user.email} />

          {/* ── Calendar sync — P6.1's door (2026-09-01). The feed has
              existed server-side since P6.1; this is the first surface that
              reaches it. The link IS the credential (a long-lived bearer
              token, one per person per workspace): it shows ONCE at mint,
              afterwards only its existence and the last-fetch receipt.

              ⚠ ABSENT UNTIL THE STATUS ANSWERS, never a disabled Create
              button. A deployment without the feed route 404s the read and
              `ics` stays null forever — which drew a permanently dead
              control, the exact drawn-and-refused shape the work address and
              the billing block already refuse. Invite mode is a preference
              and stands on its own, so it renders whatever the read did. */}
          {ics !== null || icsSyncMode === 'invites' ? (
            <>
            <div class="stg__sec stg__sec--mt">
              <span>Your calendar</span>
              <span class="stg__mini">feed or invites</span>
            </div>
            <div class="stg__row stg__row--block">
              {icsSyncMode === 'invites' ? (
                <div class="stg__row">
                  <span class="stg__k" data-testid="stg-ics-invites-mode">
                    Invites by email
                    <small>sent to {user.email} the moment a date changes &mdash; accepting files it</small>
                  </span>
                  <button
                    type="button"
                    class="stg__chip"
                    data-testid="stg-ics-mode-feed"
                    onClick={() => chooseFeedMode()}
                  >
                    Use a feed link
                  </button>
                </div>
              ) : icsMint ? (
                <>
                  <div class="stg__row">
                    <span class="stg__k" data-testid="stg-ics-url">
                      Your private feed link
                      <small>{icsMint.feedUrl}</small>
                    </span>
                    <button
                      type="button"
                      class="stg__chip"
                      data-testid="stg-ics-copy"
                      onClick={() => {
                        void navigator.clipboard?.writeText(icsMint.feedUrl);
                        setIcsCopied(true);
                      }}
                    >
                      {icsCopied ? 'Copied' : 'Copy'}
                    </button>
                  </div>
                  <p class="stg__note" data-testid="stg-ics-once">
                    Subscribe to it from Google Calendar (Other calendars &rarr; From URL) or Apple
                    Calendar (Add Subscription). Anyone holding this link can read your schedule, and
                    it shows only this once &mdash; leaving this screen forgets it.
                  </p>
                </>
              ) : ics?.active ? (
                <div class="stg__row">
                  <span class="stg__k" data-testid="stg-ics-active">
                    Sync is on
                    <small>
                      {ics.lastUsedAt
                        ? `your calendar last fetched it ${shortDate(ics.lastUsedAt)}`
                        : 'no calendar has fetched it yet'}
                    </small>
                  </span>
                  <button
                    type="button"
                    class="stg__chip"
                    data-testid="stg-ics-replace"
                    disabled={icsBusy}
                    onClick={() => void createIcsLink()}
                  >
                    New link
                  </button>
                  <button
                    type="button"
                    class="stg__chip"
                    data-testid="stg-ics-revoke"
                    disabled={icsBusy}
                    onClick={() => void turnOffIcs()}
                  >
                    Turn off
                  </button>
                </div>
              ) : (
                <div class="stg__row">
                  <span class="stg__k">
                    Not connected
                    <small>a private link your calendar app checks on its own</small>
                  </span>
                  <button
                    type="button"
                    class="stg__chip"
                    data-testid="stg-ics-create"
                    disabled={icsBusy}
                    onClick={() => void createIcsLink()}
                  >
                    {icsBusy ? 'Working…' : 'Create sync link'}
                  </button>
                </div>
              )}
              {icsSyncMode === 'feed' ? (
                <div class="stg__row">
                  <span class="stg__k">
                    Prefer email invites?
                    <small>one mail per change, sent as the date moves &mdash; replaces the feed link</small>
                  </span>
                  <button
                    type="button"
                    class="stg__chip"
                    data-testid="stg-ics-mode-invites"
                    onClick={() => void chooseInviteMode()}
                  >
                    Switch
                  </button>
                </div>
              ) : null}
              {icsError ? (
                <p class="stg__note" data-testid="stg-ics-error">
                  {icsError}
                </p>
              ) : null}
            </div>
            </>
          ) : null}

          {/* ⚑ B17d'S WORKSPACE SWITCHER, PORTED HERE AT THE REXX MERGE.
              It lived on Account, which Rexx consolidated into this screen —
              and it is the ONLY route to the Front Door tenant, where the
              funnel's registrants land. A merge that dropped it would have
              removed a door silently, which is the failure this codebase
              keeps refusing. Rendered only where App hands over the act. */}
          {onSwitchTenant ? (
            <>
              <div class="stg__sec stg__sec--mt">
                <span>Your workspaces</span>
                <span class="stg__mini">here, and elsewhere</span>
              </div>
              <div class="stg__row stg__row--block">
                <WorkspaceSwitcher activeTenantId={tenantId} onSwitch={onSwitchTenant} />
              </div>
            </>
          ) : null}

          {/* GAP 57 readiness: pack-owned cards, never every pack's —
              routes/route.ts's SETTINGS_CARD_OWNERS says whose. */}
          {settingsCardOwnedBy('planPolicy', packVertical) ? <PlanPolicyCard tenantId={tenantId} /> : null}
          {settingsCardOwnedBy('agentAccess', packVertical) ? <AgentAccessCard tenantId={tenantId} /> : null}
          {/* S1c (decision eb659c8d) — Google connections, moved here from
              Personal › Accounts. The old route redirects (App.tsx); the
              server's OAuth callback still lands there and this screen
              carries the outcome banner across. */}
          {settingsCardOwnedBy('connectedAccounts', packVertical) ? (
            <>
              <div class="stg__sec stg__sec--mt">
                <span>Connected accounts</span>
                <span class="stg__mini">Google</span>
              </div>
              <ConnectedAccounts tenantId={tenantId} outcome={connectedAccountsOutcome} />
            </>
          ) : null}
        </div>

        {/* -------------------------- the company's ---------------------

            ⚑ DECLARED, NOT ASSUMED. This whole column is an EMPLOYER's shape:
            a file the office keeps on you, licences the office verifies, leave
            the office decides. A household has no office — nobody there
            verifies a certificate or approves a parent's time off — and until
            2026-09-01 it rendered on every build regardless, so a CallToWrap
            parent read "Tickets & licences" and "you ask · office decides"
            about their own family. A pack declares `settings.memberRecord`
            or the column is absent, and the preferences column takes the
            full width (`.stg__cols--one`).

            ⚠ THE FLAG, NEVER A VERTICAL NAME. `vertical === 'rexx'` here
            would be the same hardcoding one layer down, and the next pack
            that keeps files on people would have to come and edit this file. */}
        {entry?.settings?.memberRecord ? (
          <div class="stg__col">
            <div class="stg__sec">
              <span>Your record</span>
              <span class="stg__mini">the company’s file</span>
            </div>
            {record ? (
              <>
                {recordRow('Legal name', 'name')}
                {recordRow('Mobile', 'phone')}
                {recordRow('Email', 'email')}
                {recordRow('Emergency contact', 'emergency_contact', 'Nobody sees this until it is needed')}
                {recordRow('Role', 'role')}
                {payBasisLine(record) !== null ? (
                  <div class="stg__row">
                    <span class="stg__k">Pay basis</span>
                    <span class="stg__v">
                      {payBasisLine(record)}{' '}
                      <OwnershipMark ownership={ownershipFromRule(RECORD_RULES.pay_basis, actorRoles)} />
                    </span>
                  </div>
                ) : null}

                <div class="stg__sec stg__sec--mt">
                  <span>Tickets &amp; licences</span>
                  <span class="stg__mini">you add · office verifies</span>
                </div>
                {tickets.length > 0 ? (
                  tickets.map((t) => (
                    <div key={t.id} class="stg__row">
                      <span class="stg__k">
                        {t.name}
                        <small>
                          {t.verified_by
                            ? `Verified by ${t.verified_by}${t.verified_at ? ` · ${shortDate(t.verified_at)}` : ''}`
                            : 'Not verified'}
                        </small>
                      </span>
                      {t.expires_on ? (
                        <span
                          class={`stg__v stg__expiry${daysUntil(t.expires_on) <= EXPIRY_AMBER_DAYS ? ' stg__expiry--soon' : ''}`}
                        >
                          to {t.expires_on}
                        </span>
                      ) : (
                        <span class="stg__v">✓</span>
                      )}
                    </div>
                  ))
                ) : (
                  <div class="stg__empty">Nothing on file.</div>
                )}

                <div class="stg__sec stg__sec--mt">
                  <span>Time off</span>
                  <span class="stg__mini">you ask · office decides</span>
                </div>
                {/* Ask-shaped display only ("With the office", never
                    "Pending") — the approval flow is out of C6b's scope, and
                    with no asks yet the honest render is the quiet one. */}
                <div class="stg__empty">Nothing asked.</div>
              </>
            ) : (
              <div class="stg__empty">Not on file yet.</div>
            )}
          </div>
        ) : null}
      </div>

      {/* The panel is the SAME one the Techs screen opens — imported, never
          forked — so an invite sent from here is an invite, with the same
          receipt and the same seven-day link. */}
      {inviteOpen ? (
        <InvitePanel
          tenantId={tenantId}
          roles={officeRoles}
          seat={inviteOpen === true ? null : inviteOpen}
          requiresBirthDate={requiresMemberAge}
          // ⚑ THE PANEL STAYS OPEN AFTER A SUCCESSFUL INVITE. It used to close
          // itself, which destroyed the receipt in the tick it was written —
          // AddPerformer's own lesson, paid for twice on this pack. The owner
          // closes it when they have read what happened.
          onInvited={() => {}}
          onClose={() => setInviteOpen(null)}
        />
      ) : null}

      {entry?.recordNotice ? <p class="stg__legal">{entry.recordNotice}</p> : null}
      <p class="stg__legal">{user.email}</p>
    </div>
  );
}
